import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { Pill, RestructuredPill, gbp } from "./pills";

const HOW = { manual: "Manual", sf_ref: "SF reference", company_name: "Company name", learned: "Learned reference", name_amount: "Name + amount", unique_amount: "Unique amount", name: "Name" };
const TH = "px-3 py-2 text-left font-semibold";

function Section({ title, count, total, testId, children, tone = "text-slate-600" }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={testId}>
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-left hover:bg-slate-100 transition-colors" data-testid={`${testId}-trigger`}>
          <span className="text-sm font-semibold text-slate-900">{title}</span>
          <span className={`flex items-center gap-3 text-xs font-mono font-semibold tabular-nums ${tone}`}>
            {count} {count === 1 ? "loan" : "loans"} · {gbp(total)}
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 max-h-[420px] overflow-auto rounded-lg border border-slate-200 bg-white">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

const lastRepayment = (s) => {
  const [d, amt] = String(s || "").split("<br>").map((x) => x.trim());
  return d ? `${d}${amt ? ` · £${amt}` : ""}` : "—";
};

// LoanDisk's Pending Due says nothing is left to collect today: paid yesterday, in advance, or standing credit.
export function CoveredCollapsible({ covered, total, facts }) {
  if (!covered?.length) return null;
  const rows = [...covered].sort((a, b) => (b.in_credit || 0) - (a.in_credit || 0));
  return (
    <Section title="Already covered — paid ahead / paid earlier" count={covered.length} total={total} testId="covered-section" tone="text-sky-700">
      <table className="w-full text-sm" data-testid="table-covered">
        <thead className="sticky top-0 z-10 border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
          <tr><th className={TH}>Customer</th><th className={TH}>SF#</th><th className={`${TH} !text-right`}>Scheduled £</th><th className={TH}>LoanDisk says</th><th className={TH}>Last repayment</th></tr>
        </thead>
        <tbody>
          {rows.map((l) => {
            const f = facts?.[l.sf_number];
            return (
              <tr key={l.sf_number} className="border-b hover:bg-slate-50" data-testid={`row-covered-${l.sf_number}`}>
                <td className="px-3 py-1.5 font-medium text-slate-900">{l.borrower_name}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground"><span className="inline-flex items-center gap-1.5">{l.sf_number}{f?.restructured && <RestructuredPill iteration={f.iteration} testId={`covered-restructured-${l.sf_number}`} />}</span></td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-500">{gbp(l.scheduled_amount)}</td>
                <td className="px-3 py-1.5">
                  {l.in_credit > 0
                    ? <Pill tone="sky" testId={`covered-credit-${l.sf_number}`}>In credit · {gbp(l.in_credit)}</Pill>
                    : <Pill tone="emerald" testId={`covered-paid-${l.sf_number}`}>Today's instalment paid</Pill>}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{lastRepayment(l.last_repayment)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}

export function MatchedCollapsible({ matched, total }) {
  if (!matched?.length) return null;
  const rows = [...matched].sort((a, b) => (b.loan.due_amount || 0) - (a.loan.due_amount || 0));
  return (
    <Section title="Matched & completed" count={matched.length} total={total} testId="matched-section">
      <table className="w-full text-sm" data-testid="table-matched-completed">
        <thead className="sticky top-0 z-10 border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
          <tr><th className={TH}>Customer</th><th className={TH}>SF#</th><th className={`${TH} !text-right`}>Due £</th><th className={`${TH} !text-right`}>Received £</th><th className={TH}>Credit</th><th className={TH}>Matched by</th></tr>
        </thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={`${m.loan.sf_number}-${i}`} className="border-b hover:bg-slate-50" data-testid={`row-matched-${m.loan.sf_number}`}>
              <td className="px-3 py-1.5 font-medium text-slate-900">{m.loan.borrower_name}</td>
              <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{m.loan.sf_number}</td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums">{gbp(m.loan.due_amount)}</td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-700">{gbp(m.transaction.amount)}</td>
              <td className="px-3 py-1.5 text-xs text-slate-500 truncate max-w-[16rem]" title={m.transaction.description}>{m.transaction.description}</td>
              <td className="px-3 py-1.5 text-xs text-slate-500">{HOW[m.match_type] || "Auto"}{m.match_via === "unique_name_amount" ? " + amount" : ""}{m.match_via === "previous_loan_ref" ? <span className="text-indigo-600" title={`Paid quoting ${m.paid_with_ref} (closed) — same borrower, so it covers their live loan`} data-testid={`matched-old-ref-${m.loan.sf_number}`}> · old ref {m.paid_with_ref}</span> : ""} · {m.match_score}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}
